// Presentation metadata for the Now feed's rows: the kind tag, the headline pick and which
// subjects can be assigned.
//
// This is display-only. The row's sentence (`says`) is composed server-side and is
// never rebuilt here — this module only decides WHICH string leads the row and how the
// row is labelled. Actions stay in kindActions.js.
//
// THE "DON'T HARDCODE THE KINDS" CONTRACT still holds: KIND_LABELS names only the kinds
// seen in the fn_now fixtures and code, and every other kind — including one added
// server-side tomorrow — gets a humanised tag from kindLabel(), never a blank.

// Own-property lookup: kinds are server data, so "constructor" or "toString" must not
// resolve through the prototype chain to a function.
const own = (map, key) =>
  Object.prototype.hasOwnProperty.call(map, key) ? map[key] : undefined;

/** Kind → the tag shown on the row. Only kinds evidenced in code or fixtures. */
export const KIND_LABELS = {
  promise_broken: "Broken promise",
  unanswered: "Awaiting reply",
  request_unanswered: "Open request",
  needs_assistance: "Needs help",
  payment_failed: "Payment failed",
  chase_client: "Gone quiet",
  agent_waiting: "Agent ready",
  sla_first_response: "Awaiting reply",
  sla_no_commitment: "No commitment",
  sla_gone_quiet: "Gone quiet",
};

/**
 * Turn a snake/kebab-case kind into a sentence-case phrase
 * (`renewal_upcoming` → "Renewal upcoming").
 * @param {string} kind
 * @returns {string}
 */
const humanizeKind = (kind) => {
  const words = String(kind).replace(/[_-]+/g, " ").trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : "";
};

/**
 * The tag text for a row's kind: the mapped label, else the humanised kind. Never blank —
 * a kind with no usable name reads "Update".
 * @param {string} [kind]
 * @returns {string}
 */
export const kindLabel = (kind) => {
  if (!kind) return "Update";
  return own(KIND_LABELS, kind) || humanizeKind(kind) || "Update";
};

/** Subjects that have a write for assigning an owner (fn_work_assign / fn_thread_assign). */
export const ASSIGNABLE_SUBJECTS = new Set(["task", "thread"]);

/**
 * Whether a row's subject has an assign write behind it. Commitment, message and invoice
 * subjects have none, so they never offer Assign.
 * @param {{subject_type?: string}} item
 * @returns {boolean}
 */
export const isAssignable = (item) =>
  !!item && ASSIGNABLE_SUBJECTS.has(item.subject_type);

/**
 * Whether a row should offer to be assigned (Assign, Take): nobody owns it yet and its
 * subject has an assign write. The one definition the row and its controls read.
 * @param {{owner?: string|null, owner_id?: string|null, subject_type?: string}} item
 * @returns {boolean}
 */
export const canAssign = (item) =>
  !!item && !item.owner && !item.owner_id && isAssignable(item);

const capitalize = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

/**
 * Which string leads the row, and which one is kept beside it as `detail`.
 *
 * The server's `says` leads for every kind, commitments included, verbatim. The item's
 * `title` (the thing the sentence is about) is the detail: the row keeps it reachable but
 * secondary. The title only leads when the item has no `says` at all, and then there is
 * no detail.
 *
 * @param {{says?: string, title?: string}} item
 * @returns {{text: string, detail: string|null, source: "title"|"says"}}
 *   `source` names which field `text` came from; `detail` is the other field, capitalised
 *   because titles arrive lower-cased, or null when there is none.
 */
export const rowHeadline = (item) => {
  const says = item?.says || "";
  const title = item?.title || "";
  if (!says && title) {
    return { text: capitalize(title), detail: null, source: "title" };
  }
  return { text: says, detail: title ? capitalize(title) : null, source: "says" };
};
